import {
  Box,
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Radio,
  RadioGroup,
  Stack
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import FilterCheckboxList from './FilterCheckboxList';
import { useState } from 'react';
import { ButtonText, DatePicker } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';

interface FilterItem {
  value: string;
  name: string;
}

interface FilterGroup {
  key: string;
  label: string;
  items: FilterItem[];
}

interface FiltersAccordionProps {
  filterGroups: FilterGroup[];
  selectedFilters: Record<string, string[]>;
  setSelectedFilters: (filters: Record<string, string[]>) => void;
}
const FiltersAccordion: React.FC<FiltersAccordionProps> = ({
  filterGroups,
  selectedFilters,
  setSelectedFilters
}) => {
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  return (
    <Box>
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={indices =>
          setExpandedIndices(Array.isArray(indices) ? indices : [indices])
        }
      >
        {filterGroups.map(group =>
          group.items.length > 0 ? (
            <AccordionItem key={group.key}>
              {({ isExpanded }) => {
                // Safe count of selected items
                const selectedCount = selectedFilters?.[group.key]?.length ?? 0;

                return (
                  <>
                    <AccordionButton background="actionSecondaryFill">
                      <Flex
                        flex="1"
                        textAlign="left"
                        justify="space-between"
                        align="center"
                      >
                        <Text
                          textStyle="headingItem"
                          color="actionSecondaryContent"
                        >
                          {group.label}
                          {selectedCount > 0 && ` (${selectedCount})`}
                        </Text>
                        <Box ml={2} p={2}>
                          <FontAwesomeIcon
                            icon={isExpanded ? faMinus : faPlus}
                            color="blue"
                          />
                        </Box>
                      </Flex>
                    </AccordionButton>

                    {group.key.toLowerCase() === 'datevalue' ? (
                      <AccordionPanel
                        pb={2}
                        m={2}
                        borderRadius="md"
                        bg="#F9FAFB"
                      >
                        <ButtonText
                          variant="secondary"
                          mb="2"
                          mt="2"
                          onClick={() =>
                            setSelectedFilters({
                              ...selectedFilters,
                              [group.key]: []
                            })
                          }
                        >
                          Clear Selections
                        </ButtonText>
                        <RadioGroup
                          value={selectedFilters?.[group.key]?.[0] ?? ''}
                          onChange={val =>
                            setSelectedFilters({
                              ...selectedFilters,
                              [group.key]: val ? [val] : []
                            })
                          }
                        >
                          <Stack spacing={3}>
                            {group.items.map(item => (
                              <Radio key={item.value} value={item.value}>
                                <Text textStyle="textBody">{item.name}</Text>
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>
                      </AccordionPanel>
                    ) : group.key.toLowerCase() !== 'daterange' ? (
                      <AccordionPanel
                        pb={2}
                        m={2}
                        borderRadius="md"
                        bg="#F9FAFB"
                      >
                        <FilterCheckboxList
                          name={group.key}
                          itemLabel={group.label}
                          items={group.items}
                          selectedValues={selectedFilters?.[group.key] || []}
                          onChange={values =>
                            setSelectedFilters({
                              ...selectedFilters,
                              [group.key]: values
                            })
                          }
                          clearSelection={() =>
                            setSelectedFilters({
                              ...selectedFilters,
                              [group.key]: []
                            })
                          }
                        />
                      </AccordionPanel>
                    ) : (
                      <AccordionPanel
                        pb={2}
                        m={2}
                        borderRadius="md"
                        bg="#F9FAFB"
                      >
                        <Flex
                          gap={3}
                          justifyContent={'space-between'}
                          alignItems={'center'}
                          direction={'column'}
                        >
                          <Box width="100%" textAlign="left" mt={2}>
                            <ButtonText
                              variant="secondary"
                              mb="2"
                              mt="2"
                              onClick={() =>
                                setSelectedFilters({
                                  ...selectedFilters,
                                  dateRange: []
                                })
                              }
                            >
                              Clear Selections
                            </ButtonText>
                          </Box>
                          <Flex direction={'column'} width={'100%'}>
                            <FieldLabel subText label="From" />
                            <DatePicker
                              name="fromDate"
                              value={selectedFilters.dateRange?.[0] ?? ''}
                              onChange={value =>
                                setSelectedFilters({
                                  ...selectedFilters,
                                  dateRange: [
                                    value.target.value,
                                    selectedFilters.dateRange?.[1] ?? ''
                                  ]
                                })
                              }
                              max={selectedFilters.dateRange?.[1]}
                            />
                          </Flex>
                          <Flex direction={'column'} width={'100%'}>
                            <FieldLabel subText label="To" />
                            <DatePicker
                              name="toDate"
                              value={selectedFilters.dateRange?.[1] ?? ''}
                              onChange={value =>
                                setSelectedFilters({
                                  ...selectedFilters,
                                  dateRange: [
                                    selectedFilters.dateRange?.[0] ?? '',
                                    value.target.value
                                  ]
                                })
                              }
                              min={selectedFilters.dateRange?.[0]}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </Flex>
                        </Flex>
                      </AccordionPanel>
                    )}
                  </>
                );
              }}
            </AccordionItem>
          ) : null
        )}
      </Accordion>
    </Box>
  );
};

export default FiltersAccordion;
